import { massGrams, massHasNumber, massMagnitudeMatches } from './mass-entry-contract';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { finiteNumberValidator, massCycleValidator, massUnitValidator, massValidationMessage, nonNegativeFiniteNumberValidator } from './data-entity-mass-form.validators';

@Component({
  selector: 'app-data-entity-mass-measurement',
  templateUrl: './DataEntityMassMeasurement.component.html',
  styleUrls: ['./DataEntityMassMeasurement.component.scss']
})
export class DataEntityMassMeasurementComponent implements OnChanges {
  @Input() group: FormGroup;
  @Input() index = 0;
  @Input() initiallyExpanded = false;
  @Input() idConflict = false;
  @Output() remove = new EventEmitter<void>();
  @Output() measurementIdChange = new EventEmitter<void>();
  expanded = false;
  constructor(private formBuilder: FormBuilder) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.initiallyExpanded && changes.initiallyExpanded.firstChange) {
      this.expanded = this.initiallyExpanded;
    }
  }

  toggleExpanded(): void { this.expanded = !this.expanded; }
  validationMessage(control: AbstractControl, key?: string): string { return massValidationMessage(control, key); }

  get cycles(): FormArray { return this.group.get('Cycles') as FormArray; }
  get method(): number { return Number(this.group.get('Method').value); }

  assignLegacyStage(): void {
    const stage = this.group.get('LegacyCycleStage').value;
    if (!stage) { return; }
    const legacy = this.group.get('LegacyCycles').value;
    const destination = this.group.get(stage === 'AsLeft' ? 'AsLeftCycles' : 'Cycles') as FormArray;
    if (destination.controls.some(c => ['ReferenceValues','TestValues'].some(key => (c.get(key) as FormArray).value.some(massHasNumber)))) {
      this.group.setErrors({ ...(this.group.errors || {}), legacyStageConflict: true }); return;
    }
    const restored = DataEntityMassMeasurementComponent.create(this.formBuilder, { Method: this.method, Cycles: legacy, AdjustmentPerformed: stage === 'AsLeft' ? true : false });
    this.group.setControl(stage === 'AsLeft' ? 'AsLeftCycles' : 'Cycles', restored.get('Cycles'));
    if (stage === 'AsLeft') { this.group.get('AdjustmentPerformed').setValue(true); }
    this.group.get('LegacyCycles').setValue([]);
    this.group.updateValueAndValidity();
  }
  addLeftCycle(): void {
    const cycle = (DataEntityMassMeasurementComponent.create(this.formBuilder, { Method: this.method }).get('Cycles') as FormArray).at(0);
    (this.group.get('AsLeftCycles') as FormArray).push(cycle);
  }
  get leftCycles(): FormArray { return this.group.get('AsLeftCycles') as FormArray; }
  changeAdjustment(): void { this.group.updateValueAndValidity(); }

  addCycle(): void {
    const testCount = this.method === 0 ? 1 : 2;
    this.cycles.push(this.formBuilder.group({
      ReferenceValues: this.formBuilder.array([this.numberControl(), this.numberControl()]),
      TestValues: this.formBuilder.array(Array(testCount).fill(0).map(() => this.numberControl()))
    }));
    this.group.updateValueAndValidity();
  }

  removeCycle(index: number): void { if (this.cycles.length > 1) { this.cycles.removeAt(index); } }
  values(cycle: FormGroup, name: string): FormArray { return cycle.get(name) as FormArray; }

  changeMethod(): void {
    const count = this.method === 0 ? 1 : 2;
    this.cycles.controls.concat(this.leftCycles.controls).forEach(cycle => {
      const tests = cycle.get('TestValues') as FormArray;
      // A method change must not delete observations. Only unused blank slots
      // can disappear automatically; recorded extra readings remain invalid.
      while (tests.length > count && (tests.at(tests.length - 1).value == null || tests.at(tests.length - 1).value === '')) { tests.removeAt(tests.length - 1); }
      while (tests.length < count) { tests.push(this.numberControl()); }
    });
    this.group.updateValueAndValidity();
  }
  removeExtraTest(cycle: AbstractControl, index: number): void {
    const tests = cycle.get('TestValues') as FormArray;
    if (tests.length > (this.method === 0 ? 1 : 2)) { tests.removeAt(index); }
  }

  static create(formBuilder: FormBuilder, value: any = {}): FormGroup {
    const method = value.Method == null ? null : Number(value.Method);
    const originalCycles = value.Cycles;
    const conflictingStages = Array.isArray(value.AsFoundCycles) && originalCycles && originalCycles.length && JSON.stringify(originalCycles) !== JSON.stringify(value.AsFoundCycles);
    if (Array.isArray(value.AsFoundCycles)) { value = { ...value, Cycles: value.AsFoundCycles }; }
    const unresolvedLegacy = value.AdjustmentPerformed == null && !Array.isArray(value.AsFoundCycles) && value.Cycles && value.Cycles.length;
    const cycles = unresolvedLegacy ? [] : value.Cycles || [{}];
    let nominalDisplay = value.NominalValue == null ? null : value.NominalValue;
    if (massHasNumber(nominalDisplay) && value.NominalDisplayUnit && ['mg','g','kg'].includes(value.Unit || 'g') && ['mg','g','kg'].includes(value.NominalDisplayUnit)) {
      nominalDisplay = massGrams(nominalDisplay, value.Unit || 'g') / massGrams(1, value.NominalDisplayUnit);
    }
    const stageValidator = (control: AbstractControl): any => {
      const m = control.value;
      if (!m.Cycles) { return null; }
      const errors: any = {};
      if (m.LegacyCycles && m.LegacyCycles.length) { errors.legacyUnresolved = true; }
      if (!m.Cycles.length) { errors.asFoundRequired = true; }
      const after = m.AsLeftCycles || [];
      if ((m.Cycles.length === 1 || m.AdjustmentPerformed === true && after.length === 1) &&
          (m.TheoreticalSPool == null || !Number.isFinite(Number(m.TheoreticalSPool)) || Number(m.TheoreticalSPool) < 0)) {
        errors.repeatabilityRequired = true;
      }
      try {
        const nominal = massGrams(Number(m.NominalValue), m.NominalDisplayUnit || m.Unit);
        if (nominal > 0 && m.Cycles.concat(after).some(c =>
            (c.ReferenceValues || []).concat(c.TestValues || []).some(v => v !== null && v !== '' && Number.isFinite(Number(v)) && !massMagnitudeMatches(nominal, massGrams(Number(v), m.Unit))))) {
          errors.nominalMagnitude = true;
        }
      } catch (_) { errors.nominalMagnitude = true; }
      if (m.AdjustmentPerformed === true && !after.length) { errors.stageRequired = true; }
      if (m.AdjustmentPerformed === false && after.length) { errors.unexpectedAsLeft = true; }
      const count = Number(m.Method) === 0 ? 1 : 2;
      if (m.AdjustmentPerformed === true && after.some(c => !c.ReferenceValues || c.ReferenceValues.length !== 2 || !c.TestValues || c.TestValues.length !== count)) { errors.massCycles = true; }
      return Object.keys(errors).length ? errors : null;
    };
    const result = formBuilder.group({
      LegacyCycleStage: [''], LegacyCycles: [conflictingStages ? originalCycles : unresolvedLegacy ? value.Cycles : []],
      Id: [value.Id || '', Validators.required], Name: [value.Name || ''], Marking: [value.Marking || ''],
      NominalValue: [nominalDisplay, [finiteNumberValidator(), Validators.min(Number.MIN_VALUE)]],
      NominalDisplayUnit: [value.NominalDisplayUnit || value.Unit || 'g', massUnitValidator()],
      AdjustmentPerformed: [value.AdjustmentPerformed == null ? null : value.AdjustmentPerformed, Validators.required],
      AsLeftCycles: formBuilder.array((value.AsLeftCycles || []).map(c => formBuilder.group({
        ReferenceValues: formBuilder.array(c.ReferenceValues.map(v => formBuilder.control(v, finiteNumberValidator()))),
        TestValues: formBuilder.array(c.TestValues.map(v => formBuilder.control(v, finiteNumberValidator())))
      }))),
      Unit: [value.Unit || 'g', massUnitValidator()], Method: [method, [Validators.required, c => [0,1].includes(c.value) ? null : { methodInvalid: true }]],
      TheoreticalSPool: [value.TheoreticalSPool == null ? null : value.TheoreticalSPool],
      Cycles: formBuilder.array(cycles.map((cycle: any) => formBuilder.group({
        ReferenceValues: formBuilder.array((cycle.ReferenceValues || [null, null]).map((v: number) => formBuilder.control(v, finiteNumberValidator()))),
        TestValues: formBuilder.array((cycle.TestValues || Array(method === 0 ? 1 : 2).fill(null)).map((v: number) => formBuilder.control(v, finiteNumberValidator())))
      })))
    }, { validators: [massCycleValidator(), stageValidator] });
    let previousUnit = result.get('Unit').value;
    result.get('Unit').valueChanges.subscribe(unit => {
      if (unit === previousUnit || !['mg','g','kg'].includes(unit) || !['mg','g','kg'].includes(previousUnit)) { return; }
      const factor = massGrams(1, previousUnit) / massGrams(1, unit);
      ['Cycles','AsLeftCycles'].forEach(key => (result.get(key) as FormArray).controls.forEach(c => {
        ['ReferenceValues','TestValues'].forEach(name => (c.get(name) as FormArray).controls.forEach(v => {
          if (massHasNumber(v.value)) { v.setValue(Number(v.value) * factor, { emitEvent: false }); }
        }));
      }));
      const legacy = result.get('LegacyCycles').value;
      legacy.forEach(c => ['ReferenceValues','TestValues'].forEach(key => c[key] = c[key].map(v => massHasNumber(v) ? Number(v) * factor : v)));
      const spool = result.get('TheoreticalSPool');
      if (massHasNumber(spool.value)) { spool.setValue(Number(spool.value) * factor, { emitEvent: false }); }
      previousUnit = unit;
      result.updateValueAndValidity({ emitEvent: false });
    });
    let previousNominalUnit = result.get('NominalDisplayUnit').value;
    result.get('NominalDisplayUnit').valueChanges.subscribe(unit => {
      if (unit === previousNominalUnit || !['mg','g','kg'].includes(unit) || !['mg','g','kg'].includes(previousNominalUnit)) { return; }
      const nominal = result.get('NominalValue');
      if (massHasNumber(nominal.value)) {
        nominal.setValue(massGrams(nominal.value, previousNominalUnit) / massGrams(1, unit), { emitEvent: false });
      }
      previousNominalUnit = unit;
      result.updateValueAndValidity({ emitEvent: false });
    });
    return result;
  }

  private numberControl() { return this.formBuilder.control(null, finiteNumberValidator()); }
}
